export const isManagedUserAccount = (userData, loginPayload = null) =>
  Boolean(
    userData?.is_managed ||
    userData?.is_managed_user ||
    userData?.user_context === 'managed' ||
    loginPayload?.is_managed ||
    loginPayload?.is_managed_user
  )

export const INACTIVE_ACCOUNT_MESSAGE =
  'Your account has been suspended. Please contact your administrator.'

export const isAccountActive = (user) => user?.is_active !== false

export const rejectInactiveAccount = (user, notify) => {
  if (isAccountActive(user)) return false
  if (typeof notify === 'function') {
    notify(INACTIVE_ACCOUNT_MESSAGE)
  }
  return true
}

export const getManagedOwnerBusinessName = (userData, loginPayload = null) =>
  loginPayload?.owner_business_name ||
  userData?.owner_business_name ||
  userData?.managed_permissions?.owner_business_name ||
  null

export const getManagedOwnerName = (userData, loginPayload = null) =>
  loginPayload?.owner_name ||
  userData?.owner_name ||
  userData?.managed_by_name ||
  null

export const getManagedUserOwnerLabel = (userData, loginPayload = null) =>
  getManagedOwnerBusinessName(userData, loginPayload) ||
  getManagedOwnerName(userData, loginPayload) ||
  null

export const withManagedUserContext = (userData, loginPayload = null) => {
  if (!userData) return userData

  if (!isManagedUserAccount(userData, loginPayload)) {
    return {
      ...userData,
      is_managed: false,
      is_managed_user: false,
    }
  }

  return {
    ...userData,
    is_managed: true,
    is_managed_user: true,
    owner_business_name: getManagedOwnerBusinessName(userData, loginPayload),
    owner_name: getManagedOwnerName(userData, loginPayload),
    owner_id: userData.owner_id || userData.managed_by || null,
  }
}

const isManagedUserContext = (permissions, userData) =>
  Boolean(permissions?.isManagedUser || isManagedUserAccount(userData))

const isActivityUserContext = (permissions, userData) =>
  Boolean(
    permissions?.isActivityUser ||
    userData?.user_type === 'activity_user' ||
    userData?.is_activity_user ||
    userData?.activity_role
  )

const moduleSetFromUser = (userData) => {
  const modules = userData?.managed_permissions?.accessible_modules || []
  return new Set(modules)
}

const normalizePath = (path) => (path?.startsWith('/admin') ? path : `/admin${path}`)
const normalizePathKey = (path) => normalizePath(path).toLowerCase()
const isExactOrPrefix = (pathKey, base) => pathKey === base || pathKey.startsWith(`${base}/`)

export const canAccessPath = (path, permissions, userData) => {
  const pathKey = normalizePathKey(path)

  if (pathKey === '/admin/dashboard' || pathKey === '/admin/settings' || pathKey === '/admin/support') return true
  if (!permissions) return false

  const isManaged = isManagedUserContext(permissions, userData)
  const isActivity = isActivityUserContext(permissions, userData)

  if (isManaged || isActivity) {
    const managedModules = moduleSetFromUser(userData)
    const isIn = (paths) => paths.some((route) => isExactOrPrefix(pathKey, route))

    const FARMER_PATHS = [
      '/admin/farmers',
      '/admin/newfarmer',
      '/admin/updatefarmer',
      '/admin/farmerdetail',
      '/admin/aggregatorfarmerdetail'
    ]
    const FARM_PATHS = [
      '/admin/farms',
      '/admin/newfarm',
      '/admin/updatefarm',
      '/admin/farmdetail',
      '/admin/aggregatorfarmdetail'
    ]
    const GROUP_PATHS = [
      '/admin/groups',
      '/admin/newgroup',
      '/admin/groupdetail',
      '/admin/edit-group'
    ]
    const ACTIVITY_PATHS = ['/admin/activities']
    const REQUEST_PATHS = [
      '/admin/harvest',
      '/admin/harvest-request',
      '/admin/harvest-requests',
      '/admin/harvestdetails',
      '/admin/aggregatorharvestdetails',
      '/admin/harvest-request-detail',
      '/admin/harvest-outgoing-details',
      '/admin/harvest-incoming-details'
    ]
    const CAMPAIGN_PATHS = [
      '/admin/sent-sms',
      '/admin/campaigns',
      '/admin/create-sms',
      '/admin/sms',
      '/admin/smsdetail',
      '/admin/schedule-sms'
    ]
    const SERVICE_ORDER_PATHS = [
      '/admin/service-orders',
      '/admin/service-orders/record',
      '/admin/service-order/inventory',
      '/admin/add-stock',
      '/admin/add-service',
      '/admin/edit-service',
      '/admin/edit-stock',
    ]
    const APPOINTMENT_PATHS = ['/admin/appointments', '/admin/appointment']
    const PROCESSOR_PATHS = ['/admin/mills', '/admin/processors', '/admin/aggregator', '/admin/aggretator', '/admin/aggregatormillsdetail']
    const USER_MANAGEMENT_PATHS = ['/admin/activity-users']
    const REPORT_PATHS = ['/admin/analytics']

    const canSeeGroups =
      Boolean(permissions.capabilities?.canViewGroups) ||
      managedModules.has('ha_groups') ||
      permissions.hasPermission?.('ha_groups', 'read') ||
      permissions.hasPermission?.('ha_groups', 'full_access')

    if (isIn(FARMER_PATHS)) return permissions.capabilities?.canViewFarmers || permissions.capabilities?.canAddFarmers
    if (isIn(FARM_PATHS)) return permissions.capabilities?.canViewFarms || permissions.capabilities?.canAddFarms
    if (isIn(ACTIVITY_PATHS)) return permissions.capabilities?.canViewActivities || permissions.capabilities?.canCreateActivities
    if (isIn(GROUP_PATHS)) return canSeeGroups
    if (isIn(PROCESSOR_PATHS)) return permissions.capabilities?.canViewProcessors || permissions.capabilities?.canManageProgram
    if (isIn(REPORT_PATHS)) return permissions.capabilities?.canViewReports
    if (isIn(CAMPAIGN_PATHS)) return permissions.capabilities?.canViewCampaigns
    if (isIn(SERVICE_ORDER_PATHS)) return permissions.capabilities?.canViewServiceOrders
    if (isIn(APPOINTMENT_PATHS)) return permissions.capabilities?.CanViewAppointments
    if (isIn(REQUEST_PATHS)) return permissions.capabilities?.canViewRequest
    if (isIn(USER_MANAGEMENT_PATHS)) {
      return permissions.capabilities?.canCreateUsers || permissions.capabilities?.canCreateProgramOfficers || permissions.capabilities?.canCreateFieldOfficers
    }

    return false
  }

  if (permissions.isServiceProvider || permissions.isProcessor) return true

  return true
}

